import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  SecurityContext,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { InlineEditSessionV2 } from './inline-edit-session-v2.service';

/**
 * Turns its host into an in-canvas text editor (contenteditable), so a block
 * field can be typed into straight on the v2 canvas, as in the prototype.
 *
 * The host shows `appInlineEdit` as HTML and emits the edited HTML through
 * `inlineEditChange` on every keystroke - the block wires that to its existing
 * onChange, so canvas edits take the same blockData path as right-panel ones.
 * Editing is only on while the surrounding InlineEditSessionV2 is enabled;
 * without a session the host stays read-only.
 *
 * Styles (focus outline, placeholder) come from v2-shared's `inline-edit` mixin.
 */
@Directive({
  selector: '[appInlineEdit]',
  standalone: true,
  host: { class: 'cr-inline-edit' },
})
export class InlineEditV2Directive implements OnInit, OnChanges, OnDestroy {
  @Input('appInlineEdit') value: string | null | undefined = '';
  @Input() inlineEditBlockId = '';
  @Input() inlineEditPlaceholder = '';
  /** Single-line fields leave on Enter; multi-line ones insert a <br>. */
  @Input() inlineEditMultiline = false;

  @Output() inlineEditChange = new EventEmitter<string>();

  /** The value the DOM currently shows - either rendered from the input or
   *  emitted from typing. The input is only re-rendered when it differs, so
   *  our own echo never resets the caret, but right-panel edits and undo do
   *  show up. */
  private renderedValue: string | null = null;
  /** innerHTML on focus, to tell on blur whether anything changed. */
  private htmlAtFocus = '';
  private subscription?: Subscription;

  constructor(
    private host: ElementRef<HTMLElement>,
    private sanitizer: DomSanitizer,
    @Optional() private session: InlineEditSessionV2 | null,
  ) {}

  ngOnInit(): void {
    if (this.session) {
      this.subscription = this.session.enabled$.subscribe((enabled) => this.setEditable(enabled));
    } else {
      this.setEditable(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const el = this.host.nativeElement;
    if (changes['inlineEditPlaceholder']) {
      el.setAttribute('data-placeholder', this.inlineEditPlaceholder);
      el.setAttribute('aria-placeholder', this.inlineEditPlaceholder);
    }
    if (changes['inlineEditMultiline']) {
      el.setAttribute('aria-multiline', String(this.inlineEditMultiline));
    }
    const value = this.value ?? '';
    if (value !== this.renderedValue) {
      this.render(value);
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  @HostListener('focus')
  onFocus(): void {
    this.htmlAtFocus = this.host.nativeElement.innerHTML;
    this.session?.focus(this.inlineEditBlockId);
  }

  @HostListener('blur')
  onBlur(): void {
    const el = this.host.nativeElement;
    if (el.innerHTML !== this.htmlAtFocus) {
      this.session?.commit(this.inlineEditBlockId);
    }
    // Back to the start, so the ellipsis shows the beginning of a long text again.
    el.scrollLeft = 0;
  }

  @HostListener('input')
  onInput(): void {
    const value = this.readValue();
    this.renderedValue = value;
    this.updateEmpty();
    this.inlineEditChange.emit(value);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.isComposing) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.host.nativeElement.blur();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.inlineEditMultiline) {
        this.insertLineBreak();
      } else {
        this.host.nativeElement.blur();
      }
    }
  }

  /** A field inside a link (a CTA label) would follow the link when clicked
   *  into - while it's editable, a click only places the caret. */
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (this.host.nativeElement.isContentEditable) {
      event.preventDefault();
    }
  }

  /** Blocks what the prototype doesn't offer on these fields: formatting
   *  shortcuts (Ctrl+B/I/U), drag-and-drop (which can bring in foreign HTML)
   *  and browser-made paragraphs / line breaks (Enter is handled above). */
  @HostListener('beforeinput', ['$event'])
  onBeforeInput(event: InputEvent): void {
    const type = event.inputType;
    if (
      type.startsWith('format') ||
      type === 'insertFromDrop' ||
      type === 'deleteByDrag' ||
      type === 'insertParagraph' ||
      (type === 'insertLineBreak' && !this.inlineEditMultiline)
    ) {
      event.preventDefault();
    }
  }

  /** Pastes as plain text. Single-line fields get line breaks as spaces. */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text/plain') ?? '';
    if (!text) {
      return;
    }
    const nodes: Node[] = this.inlineEditMultiline
      ? text.split(/\r\n|\r|\n/).flatMap((line, i) =>
          i === 0 ? [document.createTextNode(line)] : [document.createElement('br'), document.createTextNode(line)]
        )
      : [document.createTextNode(text.replace(/\s*[\r\n]+\s*/g, ' '))];
    if (this.insertAtCaret(nodes)) {
      this.onInput();
    }
  }

  private setEditable(enabled: boolean): void {
    const el = this.host.nativeElement;
    // A link drags itself instead of selecting the text inside it.
    const link = el.parentElement?.closest('a');
    if (enabled) {
      el.setAttribute('contenteditable', 'true');
      el.setAttribute('role', 'textbox');
      link?.setAttribute('draggable', 'false');
    } else {
      el.removeAttribute('contenteditable');
      el.removeAttribute('role');
      link?.removeAttribute('draggable');
    }
  }

  private render(value: string): void {
    const el = this.host.nativeElement;
    el.innerHTML = this.sanitizer.sanitize(SecurityContext.HTML, value) ?? '';
    this.renderedValue = value;
    this.updateEmpty();
    if (document.activeElement === el) {
      this.placeCaretAtEnd();
    }
  }

  /** The host's HTML, or '' when it holds no text. Browsers leave a stray
   *  trailing <br> behind (an emptied field, the empty last line after a
   *  line break), which isn't content. */
  private readValue(): string {
    const el = this.host.nativeElement;
    if (!el.textContent) {
      return '';
    }
    return el.innerHTML.replace(/<br\s*\/?>$/i, '');
  }

  private updateEmpty(): void {
    this.host.nativeElement.classList.toggle('cr-inline-edit-empty', !this.host.nativeElement.textContent);
  }

  private insertLineBreak(): void {
    const br = document.createElement('br');
    if (!this.insertAtCaret([br])) {
      return;
    }
    // A <br> as the very last node doesn't render a new line on its own, so
    // the caret would stay on the old one - add a second one to land on.
    if (!br.nextSibling) {
      br.after(document.createElement('br'));
    }
    this.onInput();
  }

  /** Replaces the current selection inside the host with `nodes` and puts the
   *  caret after them. False when the selection isn't inside the host. */
  private insertAtCaret(nodes: Node[]): boolean {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !this.host.nativeElement.contains(selection.anchorNode)) {
      return false;
    }
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const fragment = document.createDocumentFragment();
    nodes.forEach((node) => fragment.appendChild(node));
    const last = nodes[nodes.length - 1];
    range.insertNode(fragment);
    if (last) {
      range.setStartAfter(last);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      this.scrollToNode(last);
    }
    return true;
  }

  /** Typing scrolls a clipped single-line field to the caret by itself, but
   *  inserting nodes (paste) doesn't - so bring the inserted end into view. */
  private scrollToNode(node: Node): void {
    const el = this.host.nativeElement;
    const nodeRange = document.createRange();
    nodeRange.selectNode(node);
    const right = nodeRange.getBoundingClientRect().right;
    const boxRight = el.getBoundingClientRect().right;
    if (right > boxRight) {
      el.scrollLeft += right - boxRight;
    }
  }

  private placeCaretAtEnd(): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(this.host.nativeElement);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}
